export type StatusEffect = {
  type: 'burn' | 'freeze' | 'stun';
  duration: number; // number of turns
  damagePerTurn?: number;
  chance: number; // probability to apply (0-1)
  stackable?: boolean; // can multiple instances be applied
  maxStacks?: number;
};

export type AbilityType = 'attack' | 'defense' | 'buff' | 'debuff' | 'heal' | 'combo' | 'ultimate';

export interface Ability {
  name: string;
  type: AbilityType;
  damage?: number;
  healing?: number;
  defense?: number; // damage reduction
  energy: number;
  description: string;
  statusEffect?: StatusEffect;
  comboWith?: string[]; // names of abilities this can combo with
  criticalChance?: number; // probability of critical hit (0-1)
  dodgeChance?: number; // probability of being dodged (0-1)
  isPassive?: boolean;
  isUltimate?: boolean;
  chargeRequired?: number; // number of turns to charge ultimate
  chainBonus?: { // bonus effects when used after specific abilities
    abilities: string[];
    damageMultiplier?: number;
    statusEffectBonus?: {
      type: StatusEffect['type'];
      chance: number;
    };
  };
  synergyWith?: { // passive bonuses when certain abilities are equipped
    abilities: string[];
    effect: 'increasedDamage' | 'increasedCritical' | 'increasedStatusChance';
    amount: number;
  };
}

// Add ultimate charge mechanics
export interface UltimateState {
  currentCharge: number;
  maxCharge: number;
  isReady: boolean;
}

// Predefined status effects
export const STATUS_EFFECTS = {
  BURN: {
    type: 'burn' as const,
    duration: 3,
    damagePerTurn: 5,
    chance: 0.3
  },
  FREEZE: {
    type: 'freeze' as const,
    duration: 2,
    chance: 0.25
  },
  STUN: {
    type: 'stun' as const,
    duration: 1,
    chance: 0.2
  }
};

export const abilities: Record<string, Ability[]> = {
  'Fire Warrior': [
    { 
      name: 'Inferno Strike',
      damage: 30,
      energy: 25,
      type: 'attack',
      statusEffect: { type: 'burn', duration: 3, damage: 10 },
      description: 'A powerful strike that burns the enemy'
    },
    { 
      name: 'Flame Shield',
      defense: 20,
      energy: 20,
      type: 'defense',
      description: 'Creates a shield of flames'
    },
    { 
      name: 'Volcanic Surge',
      damage: 45,
      energy: 40,
      type: 'special',
      statusEffect: { type: 'burn', duration: 4, damage: 15 },
      description: 'Unleashes volcanic fury'
    }
  ],
  'Water Mage': [
    { 
      name: 'Tidal Wave',
      damage: 25,
      energy: 20,
      type: 'attack',
      statusEffect: { type: 'freeze', duration: 2 },
      description: 'A wave that may freeze enemies'
    },
    { 
      name: 'Healing Current',
      heal: 30,
      energy: 30,
      type: 'heal',
      description: 'Heals wounds with soothing waters'
    },
    { 
      name: 'Tsunami Force',
      damage: 40,
      energy: 35,
      type: 'special',
      statusEffect: { type: 'freeze', duration: 3 },
      description: 'Massive wave with high freeze chance'
    }
  ],
  'Earth Guardian': [
    { 
      name: 'Seismic Slam',
      damage: 35,
      energy: 30,
      type: 'attack',
      statusEffect: { type: 'stun', duration: 1 },
      description: 'Ground-shaking attack that may stun'
    },
    { 
      name: 'Stone Barrier',
      defense: 35,
      energy: 25,
      type: 'defense',
      description: 'Creates an impenetrable wall'
    },
    { 
      name: 'Tectonic Rupture',
      damage: 50,
      energy: 45,
      type: 'special',
      statusEffect: { type: 'stun', duration: 2 },
      description: 'Devastating earth attack'
    }
  ],
  'Wind Blade': [
    { 
      name: 'Cyclone Slash',
      damage: 20,
      energy: 15,
      type: 'attack',
      statusEffect: { type: 'poison', duration: 3, damage: 5 },
      description: 'Quick attack that poisons'
    },
    { 
      name: 'Wind Veil',
      defense: 15,
      energy: 15,
      type: 'defense',
      description: 'Protective wind barrier'
    },
    { 
      name: 'Hurricane Force',
      damage: 35,
      energy: 30,
      type: 'special',
      statusEffect: { type: 'poison', duration: 4, damage: 8 },
      description: 'Powerful wind attack'
    }
  ]
};

export const ultimateAbilities: Record<string, Ability> = {
  'Fire Warrior': {
    name: 'Phoenix Rebirth',
    type: 'ultimate',
    damage: 80,
    energy: 50,
    description: 'Unleash the power of the phoenix, dealing massive damage and applying burn to all enemies',
    isUltimate: true,
    chargeRequired: 5,
    statusEffect: {
      type: 'burn',
      duration: 3,
      damagePerTurn: 10,
      chance: 1
    },
    chainBonus: {
      abilities: ['Inferno Strike', 'Flame Burst'],
      damageMultiplier: 1.5,
      statusEffectBonus: {
        type: 'burn',
        chance: 0.5
      }
    }
  },
  'Water Mage': {
    name: 'Absolute Zero',
    type: 'ultimate',
    damage: 60,
    energy: 45,
    description: 'Freeze the battlefield, dealing damage and guaranteeing freeze status',
    isUltimate: true,
    chargeRequired: 4,
    statusEffect: {
      type: 'freeze',
      duration: 2,
      chance: 1
    },
    chainBonus: {
      abilities: ['Ice Shard', 'Frost Nova'],
      damageMultiplier: 1.3,
      statusEffectBonus: {
        type: 'freeze',
        chance: 0.4
      }
    }
  }
}; 